import BlogsLayout from "@/components/layout/BlogsLayout";
import {
  Box,
  CircularProgress,
  Divider,
  Grid2 as Grid,
  Stack,
  useMediaQuery,
} from "@mui/material";
import styles from "@/styles/blogs/blogsDetails.module.scss";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import Head from "next/head";
import CheckIcon from "@/public/assets/ic_check_black.svg";
import TimeIcon from "@/public/assets/ic_time.svg";
import RightIcon from "@/public/assets/ic_chevronRight_black.svg";
import { useRouter } from "next/router";
import { useDispatch, useSelector } from "react-redux";
import { blogDetailsRedux } from "@/app/store/blog-details/reducers";
import CustomLoader from "@/components/common/CustomLoader/CustomLoader";
import { blogListRedux } from "@/app/store/blog-list/reducers";
import dynamic from "next/dynamic";
import { DateFormatter } from "@/utils/DateFormatter";
import MostReadCard from "@/components/most-read-card/MostReadCard";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";
import rehypeSanitize from "rehype-sanitize";
import "highlight.js/styles/github.css";
import CodeBlock from "@/components/codeBlock/CodeBlock";
import { defaultSchema } from "hast-util-sanitize";
import { generateHeadingId } from "@/utils/generateHeadingId";

const CustomCarousel = dynamic(
  () => import("@/components/custom-carousel/CustomCarousel"),
  { ssr: false }
);

const CheckList = ({ title, isSmallScreen, divider = true }) => {
  return (
    <>
      <Box my={isSmallScreen ? 2 : 3}>
        <Stack
          direction={"row"}
          spacing={isSmallScreen ? 4 : 10}
          alignItems={"flex-start"}
        >
          <Image
            src={CheckIcon.src}
            alt={"Check Icon"}
            width={isSmallScreen ? 17 : 20}
            height={isSmallScreen ? 17 : 20}
            style={{ marginTop: "5px" }}
          />

          <p className={styles.paragraph}>{title}</p>
        </Stack>
      </Box>
      {divider && <Divider sx={{ borderColor: "#D9DBDE" }} />}
    </>
  );
};

const LANG_MAP = {
  hindi: "hi-IN",
  kannada: "kn-IN",
  bengali: "bn-IN",
  telugu: "te-IN",
  tamil: "ta-IN",
  malayalam: "ml-IN",
};

const BlogDetails = ({ locale }) => {
  const isSmallScreen = useMediaQuery("(max-width: 900px)");
  const isMobileScreen = useMediaQuery("(max-width: 767px)");
  const router = useRouter();
  const pageSlug = router.query.slug;
  const dispatch = useDispatch();

  const fetchBlogDetails = (data) =>
    dispatch(blogDetailsRedux.actions.getBlogDetailsRequest(data));

  const setBlogDetailsState = (data) =>
    dispatch(
      blogDetailsRedux.actions.setBlogDetailsState({
        key: data?.key,
        value: data?.value,
      })
    );

  const blogDetailsState = useSelector(
    (state) => state?.blogDetails?.blogDetails?.blogs?.[0]
  );
  const isInvalidSlug = useSelector(
    (state) => state?.blogDetails?.isInvalidSlug
  );

  const isBlogDetailsLoading = useSelector(
    (state) => state?.blogDetails?.isLoading
  );

  const isManualScroll = useRef(false);
  const observerRef = useRef(null);

  const [activeSection, setActiveSection] = useState("");
  const [selectedDropdown, setSelectedDropdown] = useState("");

  // Function to initialize Intersection Observer
  const setupObserver = () => {
    if (observerRef.current) observerRef.current.disconnect(); // Cleanup previous observer

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (isManualScroll.current) return; // Ignore auto scroll changes

        const visibleEntry = entries.find((entry) => entry.isIntersecting);
        console.log("visibleEntry", visibleEntry);
        if (visibleEntry) {
          setActiveSection(visibleEntry.target.id);
        }
      },
      {
        root: null,
        rootMargin: "-20% 0px -60% 0px",
        threshold: 0.1,
      }
    );

    tableOfContents.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) observerRef.current?.observe(element);
    });
  };

  useEffect(() => {
    if (activeSection) {
      setSelectedDropdown(activeSection);
    }
  }, [activeSection]);

  const handleSetActive = (to) => {
    setActiveSection(to);
  };

  const generateId = (text, index) => {
    const cleanText = text
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\p{L}\p{N}-]/gu, ""); // Unicode-safe

    if (cleanText) return cleanText;

    // fallback: lightweight unique ID
    return `heading-${index}-${Math.random().toString(36).substr(2, 6)}`;
  };

  const relatedBlogPageSize = 2;
  const [relatedBlogPage, setRelatedBlogPage] = useState(1);

  const fetchBlogList = (data) =>
    dispatch(blogListRedux.actions.getBlogListRequest(data));

  const relatedCategoryBlogList = useSelector(
    (state) => state?.blogList?.blogList?.blogs
  );

  const relatedCategoryBlogListPagination = useSelector(
    (state) => state?.blogList?.blogList?.blogs_connection?.pageInfo
  );
  const isBlogListLoading = useSelector((state) => state?.blogList?.isLoading);

  const handleNavigateToDetails = (slug) => {
    router.push(`/${slug}`);
  };

  const calculateReadingTime = (markdown = "", wordsPerMinute = 100) => {
    const text = markdown
      ?.replace(/[#*_>`~\[\](){}-]/g, "")
      ?.replace(/\n+/g, " ")
      ?.trim();

    const wordCount = text.split(/\s+/)?.filter(Boolean).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min`;
  };

  useEffect(() => {
    if (pageSlug) {
      fetchBlogDetails({ slug: pageSlug, locale });
    }
  }, [pageSlug]);

  useEffect(() => {
    // if (blogDetailsState?.category?.slug)
    fetchBlogList({
      // categorySlug: blogDetailsState?.category?.slug,
      page: relatedBlogPage,
      pageSize: relatedBlogPageSize,
      exceptSlug: blogDetailsState?.slug,
    });
  }, [
    blogDetailsState?.category?.slug,
    relatedBlogPage,
    blogDetailsState?.slug,
  ]);

  const extractHeaders = (markdown) => {
    if (typeof markdown !== "string") {
      console.error("extractHeaders error: markdown is not a string", markdown);
      return [];
    }

    const headerMatches = markdown.match(/^(#{1,6})\s(.+)/gm);

    return headerMatches
      ? headerMatches
          .map((line) => {
            const match = line.match(/^(#{1,6})\s(.+)/);
            if (!match) return null;

            const level = match[1].length;
            const heading = match[2].trim();
            const id = generateHeadingId(heading);

            return { level, heading, id };
          })
          .filter(Boolean)
      : [];
  };

  const tableOfContents = extractHeaders(blogDetailsState?.content || "");

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      isManualScroll.current = true;
      observerRef.current?.disconnect();

      window.scrollTo({
        top: element.offsetTop + 500,
        behavior: "smooth",
      });

      setActiveSection(id);
      setSelectedDropdown(id); // Update dropdown selection

      setTimeout(() => {
        isManualScroll.current = false;
        setupObserver(); // Re-enable observer
      }, 800);
    }
  };

  useEffect(() => {
    if (isInvalidSlug) {
      setBlogDetailsState({ key: "isInvalidSlug", value: false });
      router.push("/");
    }
  }, [isInvalidSlug]);

  useEffect(() => {
    if (!blogDetailsState?.content) return;
    setupObserver();
    return () => observerRef.current?.disconnect(); // Cleanup on unmount
  }, [blogDetailsState, tableOfContents]);

  // Blog SEO details
  const {
    canonicalURL,
    keywords,
    metaDescription,
    metaImage,
    metaTitle,
    metaViewport,
    metaRobots,
    structuredData,
    openGraph,
  } = blogDetailsState?.seo || {};

  return (
    <BlogsLayout navbarType={"details"}>
      <Head>
        <title>{metaTitle || "Simple Energy | Blogs"}</title>
        <meta name="description" content={metaDescription || "Blogs Details"} />
        <meta name="publisher" content="Simple Energy" />

        {canonicalURL && <link rel="canonical" href={canonicalURL} />}
        {keywords && <meta name="keywords" content={keywords} />}
        {metaViewport && <meta name="viewport" content={metaViewport} />}
        {metaRobots && <meta name="robots" content={metaRobots} />}

        {blogDetailsState?.author?.name && (
          <meta name="author" content={`${blogDetailsState?.author?.name}`} />
        )}

        {/* Open Graph Tags */}
        {openGraph?.ogTitle && (
          <meta property="og:title" content={openGraph.ogTitle} />
        )}
        {openGraph?.ogDescription && (
          <meta property="og:description" content={openGraph.ogDescription} />
        )}
        {openGraph?.ogImage?.url && (
          <meta property="og:image" content={openGraph.ogImage.url} />
        )}
        {openGraph?.ogUrl && (
          <meta property="og:url" content={openGraph.ogUrl} />
        )}
        {openGraph?.ogType && (
          <meta property="og:type" content={openGraph.ogType} />
        )}

        {/* Meta Image */}
        {metaImage?.url && (
          <>
            <meta property="og:image" content={metaImage.url} />
            <meta name="twitter:image" content={metaImage.url} />
            <link
              rel="preload"
              as="image"
              href={metaImage.url}
              fetchPriority="high"
            />
          </>
        )}

        {/*Structured Data */}
        {structuredData && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                ...structuredData,
              }),
            }}
          />
        )}
      </Head>

      {isBlogDetailsLoading ? (
        <CustomLoader />
      ) : (
        <>
          {console.log("blogDetailsState", blogDetailsState)}
          {blogDetailsState && (
            <>
              <div className={styles.headerSection}>
                <Image
                  unoptimized
                  src={
                    isMobileScreen
                      ? blogDetailsState?.mobile_media?.url
                      : blogDetailsState?.media?.url
                  }
                  alt="Banner 1"
                  fill
                  className={styles.headerImage}
                  sizes={"100vw"}
                  priority
                />
                <div className={styles.overlayWrapper}>
                  <div className={styles.overlayContent}>
                    <Grid container>
                      <Grid size={{ sm: 0, md: 3.65, lg: 3.65 }}></Grid>
                      <Grid size={{ sm: 12, md: 8.35, lg: 8.35 }}>
                        <Stack
                          sx={{
                            alignItems: "flex-start",
                            width: isSmallScreen ? "100%" : "85%",
                          }}
                        >
                          <div
                            style={{
                              position: "inherit",
                            }}
                          >
                            <h1>{blogDetailsState?.title}</h1>
                          </div>

                          <Grid
                            conatiner
                            spacing={2}
                            style={{
                              // marginTop: "10px",
                              display: "flex",
                              columnGap: "10px",
                              justifyContent: "center",
                            }}
                            my={isSmallScreen ? 2.5 : 4}
                          >
                            <Grid>
                              {blogDetailsState?.author ? (
                                <div
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    background: blogDetailsState?.author?.avatar
                                      ?.url
                                      ? "transparent"
                                      : "red",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    borderRadius: "50%",
                                    padding: "8px 11px",
                                    position: "relative",
                                  }}
                                >
                                  {blogDetailsState?.author?.avatar?.url ? (
                                    <Image
                                      unoptimized
                                      src={
                                        blogDetailsState?.author?.avatar?.url
                                      }
                                      fill
                                      alt={
                                        blogDetailsState?.author?.avatar
                                          ?.name || "Author avatar"
                                      }
                                      style={{
                                        borderRadius: "50%",
                                      }}
                                      sizes={"100vw"}
                                      priority
                                    />
                                  ) : (
                                    <h1
                                      style={{
                                        fontSize: "12px",
                                        margin: 0,
                                        textAlign: "center",
                                        fontFamily: "Inter",
                                      }}
                                    >
                                      {blogDetailsState?.author?.name[0]}
                                    </h1>
                                  )}
                                </div>
                              ) : (
                                ""
                              )}
                            </Grid>

                            <Grid
                              textAlign={"start"}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "12px",
                                  fontFamily: "Inter",
                                }}
                              >
                                {blogDetailsState?.author?.name}
                              </p>

                              <Stack direction={"row"} spacing={1}>
                                <p
                                  style={{
                                    fontSize: "12px",
                                    fontFamily: "Inter",
                                    fontWeight: 400,
                                    color: "#B9BBBD",
                                  }}
                                >
                                  {blogDetailsState?.publishedAt &&
                                    `${
                                      blogDetailsState?.publishedAt &&
                                      ` ${DateFormatter(
                                        blogDetailsState?.publishedAt
                                      )}`
                                    }`}
                                </p>

                                {blogDetailsState?.content?.length ? (
                                  <Stack direction={"row"} spacing={0.5}>
                                    <Image
                                      src={TimeIcon.src}
                                      alt={"Clock Icon"}
                                      width={15}
                                      height={15}
                                    />
                                    <p
                                      style={{
                                        fontSize: "12px",
                                        fontFamily: "Inter",
                                        fontWeight: 400,
                                        color: "#B9BBBD",
                                      }}
                                    >
                                      {calculateReadingTime(
                                        blogDetailsState?.content
                                      )}
                                    </p>
                                  </Stack>
                                ) : null}
                              </Stack>
                            </Grid>
                          </Grid>
                        </Stack>
                      </Grid>
                    </Grid>
                  </div>
                </div>
              </div>

              {/* Blog details */}
              <Box
                m={isSmallScreen ? 2 : 4}
                className={styles.blogDetailsWrapper}
                py={isSmallScreen ? 2 : 5}
              >
                <Grid container spacing={3}>
                  <Grid
                    size={{ sm: 0, md: 3.6, lg: 3.6 }}
                    sx={{
                      position: "sticky",
                      top: 100,
                      height: "min-content",
                    }}
                  >
                    {!isSmallScreen && (
                      <Stack
                        direction={"column"}
                        justifyContent={"center"}
                        alignItems={"center"}
                      >
                        <div>
                          {tableOfContents?.map((heading, index) => (
                            <p
                              key={heading.id}
                              onClick={() => scrollToSection(heading.id)}
                              className={
                                activeSection === heading.id
                                  ? styles.pageContentTextActive
                                  : styles.pageContentText
                              }
                            >
                              {heading.heading}
                            </p>
                          ))}
                        </div>
                      </Stack>
                    )}
                  </Grid>
                  <Grid
                    size={{ sm: 12, md: 8, lg: 8 }}
                    sx={{
                      height: "100%",
                      width: "100%",
                    }}
                    id="scrollable-container"
                  >
                    <Box
                      sx={{ width: isSmallScreen ? "100%" : "85%" }}
                      mt={-6}
                      mb={isSmallScreen ? 5 : 2}
                      className={styles.contentContainer}
                    >
                      <MarkdownRenderer content={blogDetailsState?.content} />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}

          {(isBlogListLoading || relatedCategoryBlogList?.length > 0) && (
            <Box className={styles.relatedBlogsWrapper}>
              <Box className={styles.relatedBlogsContent}>
                <div className={styles.relatedBlogHeader}>
                  <p>Recommended Blogs</p>
                  <div
                    className={styles.relatedBlogsControlIcons}
                    style={{
                      pointerEvents: isBlogListLoading ? "none" : "auto",
                    }}
                  >
                    <button
                      style={{
                        border: "none",
                        background: "none",
                        opacity: relatedBlogPage === 1 ? 0.2 : 1,
                        cursor: relatedBlogPage === 1 ? "text" : "pointer",
                      }}
                      onClick={() => setRelatedBlogPage((prev) => prev - 1)}
                      disabled={relatedBlogPage === 1}
                    >
                      <Image
                        className={styles.leftIcon}
                        src={RightIcon.src}
                        alt={"Left Icon"}
                        width={30}
                        height={30}
                      />
                    </button>

                    <button
                      style={{
                        border: "none",
                        background: "none",
                        cursor:
                          relatedBlogPage >=
                          relatedCategoryBlogListPagination?.pageCount - 1
                            ? "text"
                            : "pointer",
                        opacity:
                          relatedBlogPage >=
                          relatedCategoryBlogListPagination?.pageCount - 1
                            ? 0.2
                            : 1,
                      }}
                      onClick={() => {
                        setRelatedBlogPage((prev) => prev + 1);
                      }}
                      disabled={
                        relatedBlogPage >=
                        relatedCategoryBlogListPagination?.pageCount - 1
                      }
                    >
                      <Image
                        className={styles.rightIcon}
                        src={RightIcon.src}
                        alt={"Left Icon"}
                        width={30}
                        height={30}
                      />
                    </button>
                  </div>
                </div>

                {isBlogListLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "1rem",
                      alignItems: "center",
                      height: "300px",
                    }}
                  >
                    <CircularProgress sx={{ color: "#151617" }} />
                  </Box>
                ) : (
                  <Grid
                    container
                    columnGap={2}
                    spacing={3}
                    mt={isSmallScreen ? 2 : 4}
                  >
                    {relatedCategoryBlogList?.map((blog, index) => (
                      <Grid key={index} size={{ sm: 12, md: 6, lg: 6 }}>
                        {isSmallScreen ? (
                          <MostReadCard
                            key={index}
                            theme={"light"}
                            image={blog?.media?.url}
                            researchDetails={
                              !!blog?.author?.name &&
                              blog?.publishedAt &&
                              `${blog?.author?.name} ${
                                blog?.publishedAt &&
                                ` / ${DateFormatter(blog?.publishedAt)}`
                              }`
                            }
                            title={blog?.title}
                            description={blog?.short_description}
                            onClick={() => handleNavigateToDetails(blog?.slug)}
                          />
                        ) : (
                          <CustomCarousel
                            carouselType={"blogList"}
                            blogDetails={{
                              images: blog?.media,
                              title: blog?.title,
                              author:
                                !!blog?.author?.name &&
                                blog?.publishedAt &&
                                `${blog?.author?.name} ${
                                  blog?.publishedAt &&
                                  ` / ${DateFormatter(blog?.publishedAt)}`
                                }`,
                            }}
                            onClick={() => handleNavigateToDetails(blog?.slug)}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Box>
          )}
        </>
      )}
    </BlogsLayout>
  );
};

const MarkdownRenderer = ({ content }) => {
  const headingComponents = {};

  for (let i = 1; i <= 6; i++) {
    headingComponents[`h${i}`] = ({ children }) => {
      const text = String(children);
      const id = generateHeadingId(text);

      return React.createElement(`h${i}`, { id }, children);
    };
  }

  const customSanitizeSchema = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames || []), "u"],
  };

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkRehype]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, customSanitizeSchema]]}
      components={{
        ...headingComponents,
        code({ className, children, ...props }) {
          return (
            <CodeBlock className={className} value={children}>
              {children}
            </CodeBlock>
          );
        },
        p({ children }) {
          const newChildren = React.Children.toArray(children);
          return (
            <p>
              {newChildren.map((child, index) => {
                if (
                  typeof child === "string" &&
                  child.trim().startsWith("&&")
                ) {
                  const cleanedText = child.trim().replace(/^&&\s*/, "");
                  return (
                    <div key={index} className={styles.imageDescription}>
                      {cleanedText}
                    </div>
                  );
                }
                return child;
              })}
            </p>
          );
        },
        a({ href, children, ...props }) {
          return (
            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
              {children}
            </a>
          );
        },
      }}
    >
      {content}
    </Markdown>
  );
};

export const getServerSideProps = async (context) => {
  const path = context.resolvedUrl || "";
  let detectedLang = "en";

  // Regex to extract language from slug like "-hindi", "-telugu"
  const match = path.match(
    /-(hindi|kannada|bengali|telugu|tamil|malayalam)\b/i
  );

  if (match && match[1]) {
    const langKey = match[1].toLowerCase();
    detectedLang = LANG_MAP[langKey] || "en";
  }

  return {
    props: {
      locale: detectedLang,
    },
  };
};

export default BlogDetails;
